import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../config/supabase';
import { useAuth } from '../contexts/AuthContext';
import { useEditGuard } from './useEditGuard';
import { track } from '../utils/analytics';


export interface CalendarEvent {
  id: number | string;
  title: string;
  notes: string;
  display_order?: number;
  dates: string[];
  calendar_id?: string | null;
  color: {
    bg: string;
    gradient?: string;
    name: string;
  };
  recurrence_rule?: any;
  parent_event_id?: string;
  all_day?: boolean;
  start_time?: string | null;
  end_time?: string | null;
  isImported?: boolean;
  importedCalendarUrl?: string;
  full_size?: boolean;
  space_id?: string | null;
  created_by?: string;
  created_at?: string;
}

interface UseSupabaseEventsOptions {
  spaceId?: string | null; // null = personal space, undefined = not filtering
}

export function useSupabaseEvents(options: UseSupabaseEventsOptions = {}) {
  const allowEdit = useEditGuard();
  const { spaceId } = options;
  const { user } = useAuth();
  const [events, setEvents] = useState<CalendarEvent[]>([]);  // Changed Event to CalendarEvent
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Wrap fetchEvents in useCallback
  const fetchEvents = useCallback(async () => {
    if (!user) {
      setEvents([]);
      setLoading(false);
      return;
    }

    try {
      let query = supabase
        .from('events')
        .select('*')
        .order('created_at', { ascending: false });

      // Filter by space
      if (spaceId === null) {
        // Personal space: only events with no space_id AND owned by user
        query = query.is('space_id', null).eq('user_id', user.id);
      } else if (spaceId !== undefined) {
        // Specific shared space
        query = query.eq('space_id', spaceId);
      } else {
        // No filter specified - get user's personal events only (backwards compatible)
        query = query.is('space_id', null).eq('user_id', user.id);
      }

      const { data, error } = await query;

      if (error) throw error;

      const formattedEvents: CalendarEvent[] = (data || []).map((event: any) => ({
        id: event.id,
        title: event.title,
        notes: event.notes || '',
        dates: event.dates,
        calendar_id: event.calendar_id || null,
        color: event.color,
        recurrence_rule: event.recurrence_rule,
        parent_event_id: event.parent_event_id,
        space_id: event.space_id,
        created_by: event.created_by,
        created_at: event.created_at,
        all_day: event.all_day ?? true,
        start_time: event.start_time,
        end_time: event.end_time,
      }));

      setEvents(formattedEvents);
    } catch (err) {
      console.error('Error fetching events:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, [user, spaceId]);

  // Add new event
  const addEvent = async (event: Omit<CalendarEvent, 'id'>) => {
    if (!user) return;
    if (!allowEdit()) return;

    // Optimistic: add to state immediately with a temporary ID
    const tempId = `temp-${Date.now()}`;
    const optimisticEvent: CalendarEvent = {
      id: tempId,
      title: event.title,
      notes: event.notes || '',
      dates: event.dates,
      color: event.color,
      calendar_id: event.calendar_id,
      recurrence_rule: event.recurrence_rule,
      parent_event_id: event.parent_event_id,
      space_id: spaceId || null,
      created_by: user.id,
      all_day: event.all_day ?? true,
      start_time: event.start_time || null,
      end_time: event.end_time || null,
      full_size: event.full_size ?? false,
    };
    setEvents(current => [optimisticEvent, ...current]);

    try {
      // Prepare the event data with all fields
      const eventData: any = {
        user_id: user.id,
        created_by: user.id,
        title: event.title,
        notes: event.notes,
        dates: event.dates,
        calendar_id: event.calendar_id || null,
        color: event.color,
        space_id: spaceId || null,
        all_day: event.all_day ?? true,
        start_time: event.start_time || null,
        end_time: event.end_time || null,
        full_size: event.full_size ?? false,
      };

      if (event.recurrence_rule) {
        eventData.recurrence_rule = event.recurrence_rule;
      }
      if (event.parent_event_id) {
        eventData.parent_event_id = event.parent_event_id;
      }

      const { data, error } = await supabase
        .from('events')
        .insert([eventData])
        .select()
        .single();

      if (error) throw error;

      // Replace optimistic event with real one (swap temp ID for DB ID)
      setEvents(current => current.map(e =>
        e.id === tempId ? { ...e, id: data.id, created_at: data.created_at } : e
      ));

      track('event_created');

      return { ...optimisticEvent, id: data.id, created_at: data.created_at } as CalendarEvent;
    } catch (err) {
      // Rollback: remove the optimistic event
      setEvents(current => current.filter(e => e.id !== tempId));
      console.error('Error adding event:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  };

  // Batch add events
  const addEventBatch = async (eventsToAdd: Omit<CalendarEvent, 'id'>[]) => {
    if (!user || eventsToAdd.length === 0) return;
    if (!allowEdit()) return;

    try {
      const eventData = eventsToAdd.map(event => ({
        user_id: user.id,
        created_by: user.id,
        title: event.title,
        notes: event.notes,
        dates: event.dates,
        calendar_id: event.calendar_id || null,
        color: event.color,
        recurrence_rule: event.recurrence_rule || null,
        parent_event_id: event.parent_event_id || null,
        space_id: spaceId || null,
        all_day: event.all_day ?? true,
        start_time: event.start_time || null,
        end_time: event.end_time || null,
        full_size: event.full_size ?? false,
      }));

      const { data, error } = await supabase
        .from('events')
        .insert(eventData)
        .select();

      if (error) throw error;

      const newEvents: CalendarEvent[] = (data || []).map(d => ({
        id: d.id,
        title: d.title,
        notes: d.notes || '',
        dates: d.dates,
        calendar_id: d.calendar_id || null,
        color: d.color,
        recurrence_rule: d.recurrence_rule,
        parent_event_id: d.parent_event_id,
        space_id: d.space_id,
        created_by: d.created_by,
        created_at: d.created_at,
        all_day: d.all_day ?? true,
        start_time: d.start_time,
        end_time: d.end_time,
        full_size: d.full_size ?? false,
      }));

      setEvents(current => [...newEvents, ...current]);
      return newEvents;
    } catch (err) {
      console.error('Error batch adding events:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  };

  // Update event (optimistic — updates UI immediately, DB in background)
  const updateEvent = async (id: string | number, updates: Partial<CalendarEvent>) => {
    if (!user) return;
    if (!allowEdit()) return;

    // Optimistic: update state immediately
    let previousEvent: CalendarEvent | undefined;
    setEvents(current => current.map(event => {
      if (event.id === id) {
        previousEvent = event;
        return { ...event, ...updates };
      }
      return event;
    }));

    try {
      const updateData: any = {
        title: updates.title,
        notes: updates.notes,
        dates: updates.dates,
        color: updates.color,
        updated_at: new Date().toISOString(),
      };

      if ('calendar_id' in updates) {
        updateData.calendar_id = updates.calendar_id;
      }
      if (updates.all_day !== undefined) {
        updateData.all_day = updates.all_day;
      }
      if ('start_time' in updates) {
        updateData.start_time = updates.start_time;
      }
      if ('end_time' in updates) {
        updateData.end_time = updates.end_time;
      }
      if (updates.full_size !== undefined) {
        updateData.full_size = updates.full_size;
      }
      if (updates.recurrence_rule !== undefined) {
        updateData.recurrence_rule = updates.recurrence_rule;
      }
      if (updates.parent_event_id !== undefined) {
        updateData.parent_event_id = updates.parent_event_id;
      }

      const { data, error } = await supabase
        .from('events')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;
      return data;
    } catch (err) {
      // Rollback
      if (previousEvent) {
        setEvents(current => current.map(e => e.id === id ? previousEvent! : e));
      }
      console.error('Error updating event:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  };

  // Delete event (optimistic — removes from UI immediately, DB in background)
  const deleteEvent = async (id: string | number) => {
    if (!user) return;
    if (!allowEdit()) return;

    // Optimistic: remove from state immediately
    let removedEvent: CalendarEvent | undefined;
    setEvents(current => {
      removedEvent = current.find(e => e.id === id);
      return current.filter(e => e.id !== id);
    });

    try {
      const { error } = await supabase
        .from('events')
        .delete()
        .eq('id', id);

      if (error) throw error;
    } catch (err) {
      // Rollback: re-add the event
      if (removedEvent) {
        setEvents(current => [...current, removedEvent!]);
      }
      console.error('Error deleting event:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  };

  // Batch delete events
  const deleteEventBatch = async (ids: (string | number)[]) => {
    if (!user || ids.length === 0) return;
    if (!allowEdit()) return;

    try {
      // RLS policies handle access control - no need to filter by user_id
      const { error } = await supabase
        .from('events')
        .delete()
        .in('id', ids);

      if (error) throw error;

      // Update state once for all deletions
      setEvents(current => current.filter(event => !ids.includes(event.id)));
    } catch (err) {
      console.error('Error deleting events:', err);
      setError(err instanceof Error ? err.message : 'Unknown error');
      throw err;
    }
  };

  // Clear events immediately when space changes to prevent cross-space bleed
  useEffect(() => {
    setEvents([]);
  }, [spaceId]);

  // Set up real-time subscription
  useEffect(() => {
    if (!user) return;

    // Capture the spaceId for this effect instance so late callbacks can validate
    const activeSpaceId = spaceId;

    fetchEvents();

    // Determine the channel and filter based on space
    const channelName = spaceId
      ? `events:space_id=eq.${spaceId}`
      : `events:user_personal:${user.id}`;

    const filter = spaceId
      ? `space_id=eq.${spaceId}`
      : `user_id=eq.${user.id}`;

    // Subscribe to changes
    const subscription = supabase
      .channel(channelName)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'events',
          filter: filter,
        },
        (payload) => {
          if (payload.eventType === 'INSERT') {
            const newEvent = payload.new as any;

            // Validate event belongs to the active space view
            if (activeSpaceId === null && newEvent.space_id !== null) return;
            if (activeSpaceId !== null && newEvent.space_id !== activeSpaceId) return;

            // Skip if already present (optimistic add from addEvent)
            setEvents(current => {
              if (current.some(e => e.id === newEvent.id)) return current;
              return [
                {
                  id: newEvent.id,
                  title: newEvent.title,
                  notes: newEvent.notes || '',
                  dates: newEvent.dates,
                  calendar_id: newEvent.calendar_id || null,
                  color: newEvent.color,
                  recurrence_rule: newEvent.recurrence_rule,
                  parent_event_id: newEvent.parent_event_id,
                  space_id: newEvent.space_id,
                  created_by: newEvent.created_by,
                  created_at: newEvent.created_at,
                  all_day: newEvent.all_day ?? true,
                  start_time: newEvent.start_time,
                  end_time: newEvent.end_time,
                  full_size: newEvent.full_size ?? false,
                },
                ...current,
              ];
            });
          } else if (payload.eventType === 'UPDATE') {
            const updatedEvent = payload.new as any;
            setEvents(current =>
              current.map(event =>
                event.id === updatedEvent.id
                  ? {
                    id: updatedEvent.id,
                    title: updatedEvent.title,
                    notes: updatedEvent.notes || '',
                    dates: updatedEvent.dates,
                    calendar_id: updatedEvent.calendar_id || null,
                    color: updatedEvent.color,
                    recurrence_rule: updatedEvent.recurrence_rule,
                    parent_event_id: updatedEvent.parent_event_id,
                    space_id: updatedEvent.space_id,
                    created_by: updatedEvent.created_by,
                    created_at: updatedEvent.created_at,
                    all_day: updatedEvent.all_day ?? true,
                    start_time: updatedEvent.start_time,
                    end_time: updatedEvent.end_time,
                    full_size: updatedEvent.full_size ?? false,
                  }
                  : event
              )
            );
          } else if (payload.eventType === 'DELETE') {
            const deletedEvent = payload.old as any;
            setEvents(current =>
              current.filter(event => event.id !== deletedEvent.id)
            );
          }
        }
      )
      .subscribe();

    return () => {
      subscription.unsubscribe();
    };
  }, [user, spaceId, fetchEvents]);

  return {
    events,
    loading,
    error,
    addEvent,
    addEventBatch,
    updateEvent,
    deleteEvent,
    deleteEventBatch,
    refetch: fetchEvents,
  };
}